import React from 'react';

import { CheckboxTick } from '@components/Forms/CheckboxTick';
import { type ClassMap, makeMuiStyles, useCommonStyles } from '@styles';
import { useErrorMessage } from '@utils/forms';
import clsx from 'clsx';
import { useField } from 'formik';

const useStyles = makeMuiStyles((theme, common) => ({
  checkboxInputContainer: {
    ...common.textGray500,
    ...common.middleText,
    ...common.flexInline,
    ...common.itemsCenter,
  },
  checkbox: {
    position: 'relative',
    pointerEvents: 'none',
    height: 13,
    width: 13,
  },
  checkboxHidden: {
    ...common.baseButton,
    opacity: 0,
    marginLeft: -13,
    height: 13,
    width: 13,
  },
}));

export interface CheckboxInputProps<T> {
  name: string;
  id?: string;
  value: T;
  label: React.ReactNode;
  classes?: ClassMap<'radio' | 'container' | 'checkbox'>;
}

export const CheckboxInput = <T extends any>(props: CheckboxInputProps<T>) => {
  const { name, id = name, value, label, classes: customClasses = {} } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const [field, , helpers] = useField<T[]>(name);

  const checked = field.value.includes(value);

  const onClick = () => {
    helpers.setTouched(true);
    if (checked) {
      helpers.setValue(field.value.filter((v) => v !== value));
    } else {
      helpers.setValue([...field.value, value]);
    }
  };

  const errorMessage = useErrorMessage(field.name);

  return (
    <div className={clsx(classes.checkboxInputContainer, customClasses.container)}>
      <span className={clsx(classes.checkbox, customClasses.checkbox)}>
        <CheckboxTick
          style={{ height: 'inherit', width: 'inherit' }}
          checked={checked}
          error={errorMessage !== undefined}
        />
      </span>
      <input
        id={id}
        name={name}
        type="radio"
        checked={checked}
        className={clsx(classes.checkboxHidden, customClasses.radio)}
        onClick={onClick}
      />
      <span style={{ display: 'inline-block', width: 6 }} />
      {label}
    </div>
  );
};
